/**
 * Programme scheduling — §11.3, §11.4, §11.5.
 *
 * Everything here is a pure function of its arguments plus an explicit `now`, so the
 * entire schedule is testable at a fixed instant with no clock faking.
 *
 * The governing rule (§11.1):
 *   THE PROGRAMME SUPPLIES THE SCOPE AND THE DAILY CEILING.
 *   SM-2 SUPPLIES THE ORDER WITHIN THAT CEILING.
 * Positional presets compute their block from the cursor and use SM-2 only to pull
 * LAPSED pages forward.
 */
import {
  DAY_MS,
  DEFAULT_MAX_CARRY_OVER,
  MAX_AHEAD_DAYS,
  TOTAL_LINES,
  TOTAL_PAGES,
} from './constants'
import {
  firstPageOfJuz,
  fromAbsoluteLine,
  linesBefore,
  linesOnPage,
  toAbsoluteLine,
} from './navigation'
import { dayStamp, dueCards } from './srs'
import { methodById, type HifzMethod, type MethodKind, type VolumeUnit } from './methods'
import type { LinePosition, Meta, SrsCard } from './types'

// ---------------------------------------------------------------------------
// Stored shapes (§11.2)
// ---------------------------------------------------------------------------

export type ProgramScope =
  | { type: 'mushaf' }
  | { type: 'juz'; from: number; to: number }
  | { type: 'surah'; from: number; to: number }
  | { type: 'pages'; from: number; to: number }

export interface VolumeSegment {
  /** Day stamp this segment takes effect. */
  from: number
  unit: VolumeUnit
  amount: number
  review: number
}

export interface Program {
  id: string
  methodId: string
  /** User-editable, seeded from the method. */
  name: string
  kind: MethodKind
  scope: ProgramScope
  /** APPEND-ONLY, ascending by `from`. See the note on `expectedLinesBy`. */
  volumes: VolumeSegment[]
  /** Next position to work on. */
  cursor: LinePosition
  /** The cursor at creation. */
  startAt: LinePosition
  /** 0 = Sunday. */
  restDays: number[]
  /** In daily volumes. */
  maxCarryOver: number
  state: 'active' | 'paused' | 'done'
  startedOn: number
  createdAt: number
}

// ---------------------------------------------------------------------------
// §11.3 — scheduler arithmetic
// ---------------------------------------------------------------------------

const LINES_PER_JUZ = TOTAL_LINES / 30
const LINES_PER_AYAH = TOTAL_LINES / 6236 // ≈ 1.45

/** "How much does the plan ask for on an average day". */
export function nominalLines(unit: VolumeUnit, amount: number): number {
  switch (unit) {
    case 'line':
      return amount
    case 'page':
      return amount * 15
    case 'juz':
      return amount * LINES_PER_JUZ
    case 'ayah':
      return amount * LINES_PER_AYAH
  }
}

/**
 * The EXACT extent of today's portion, as a line count starting at `fromLine`.
 *
 * `page` means "to the end of this page, no further" when the cursor sits mid-page —
 * not "fifteen lines from wherever you are".
 */
export function volumeSpan(
  unit: VolumeUnit,
  amount: number,
  fromLine: number,
  index: Meta,
): number {
  if (amount <= 0) return 0
  const at = fromAbsoluteLine(fromLine)

  switch (unit) {
    case 'line':
      return Math.round(amount)

    case 'page': {
      let span = linesOnPage(at.page) - at.line + 1
      for (let i = 1; i < Math.round(amount); i += 1) {
        const page = at.page + i
        if (page > TOTAL_PAGES) break
        span += linesOnPage(page)
      }
      return span
    }

    case 'juz': {
      const currentJuz = index.pages.find((p) => p.page === at.page)?.juz ?? 1
      const targetJuz = currentJuz + Math.round(amount)
      const targetPage = targetJuz > 30 ? TOTAL_PAGES + 1 : firstPageOfJuz(index, targetJuz)
      return Math.max(0, linesBefore(targetPage) - fromLine)
    }

    case 'ayah':
      // The one approximation: mapping ayat to lines needs per-page word data not
      // loaded here.
      return Math.max(1, Math.round(amount * LINES_PER_AYAH))
  }
}

export function scopePages(scope: ProgramScope, index: Meta): { from: number; to: number } {
  switch (scope.type) {
    case 'mushaf':
      return { from: 1, to: TOTAL_PAGES }
    case 'pages':
      return { from: Math.max(1, scope.from), to: Math.min(TOTAL_PAGES, scope.to) }
    case 'juz': {
      const from = firstPageOfJuz(index, scope.from)
      const to = scope.to >= 30 ? TOTAL_PAGES : firstPageOfJuz(index, scope.to + 1) - 1
      return { from, to: Math.max(from, to) }
    }
    case 'surah': {
      const first = index.chapters.find((c) => c.id === scope.from)
      const last = index.chapters.find((c) => c.id === scope.to)
      return {
        from: first ? first.pages[0] : 1,
        to: last ? last.pages[1] : TOTAL_PAGES,
      }
    }
  }
}

export const isRestDay = (day: number, restDays: readonly number[]): boolean =>
  restDays.includes(new Date(day * DAY_MS).getUTCDay())

/**
 * Inclusive, minus rest days.
 *
 * Counted by WHOLE WEEKS PLUS A REMAINDER, not by iterating: a plan running for years
 * would otherwise walk thousands of days on every render.
 */
export function activeDays(from: number, to: number, restDays: readonly number[]): number {
  if (to < from) return 0
  const total = to - from + 1
  const rest = new Set(restDays)
  if (rest.size === 0) return total
  if (rest.size >= 7) return 0

  const weeks = Math.floor(total / 7)
  let count = weeks * (7 - rest.size)
  const remainderStart = from + weeks * 7
  for (let i = 0; i < total % 7; i += 1) {
    if (!isRestDay(remainderStart + i, restDays)) count += 1
  }
  return count
}

/**
 * A piecewise sum over the append-only segments, so THE PAST KEEPS THE EXPECTATION IT
 * WAS ACTUALLY SET UNDER.
 *
 * With one mutable volume field, raising 1 → 4 pages retroactively rewrites every
 * past day's expectation, "you are 30 pages behind" appears out of nowhere for days
 * the user actually completed, the adherence number becomes a lie, and the user stops
 * trusting the screen.
 */
export function expectedLinesBy(program: Program, day: number): number {
  const segments = [...program.volumes].sort((a, b) => a.from - b.from)
  let total = 0
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]!
    const nextFrom = segments[i + 1]?.from ?? Infinity
    const from = Math.max(seg.from, program.startedOn)
    const to = Math.min(nextFrom - 1, day)
    total += nominalLines(seg.unit, seg.amount) * activeDays(from, to, program.restDays)
  }
  return total
}

export function currentSegment(program: Program, day: number): VolumeSegment {
  const segments = [...program.volumes].sort((a, b) => a.from - b.from)
  let found = segments[0]
  for (const seg of segments) {
    if (seg.from <= day) found = seg
  }
  return found ?? { from: program.startedOn, unit: 'page', amount: 1, review: 10 }
}

export interface Adherence {
  total: number
  done: number
  expected: number
  perDay: number
  debt: number
  ahead: number
  percent: number
  daysLeft: number | null
  finishesOn: number | null
  scope: { from: number; to: number }
  startLine: number
  endLine: number
}

export function adherence(program: Program, index: Meta, now: number): Adherence {
  const today = dayStamp(now)
  const scope = scopePages(program.scope, index)
  const endLine = linesBefore(scope.to + 1)

  /**
   * WHY `startAt` EXISTS. Progress is measured from where the PROGRAMME began, not
   * from the start of the scope. A reader who already knows the first fifty pages and
   * starts a mushaf-wide programme on page 51 has completed none of THEIR plan.
   * Measuring from the scope would credit them 750 lines they never did — and because
   * banked work offsets the daily obligation, the programme would be permanently
   * "caught up" and would never issue a ward again. (This was a real bug.)
   */
  const startLine = Math.min(
    endLine,
    Math.max(linesBefore(scope.from), toAbsoluteLine(program.startAt)),
  )
  const total = Math.max(0, endLine - startLine)
  const done = Math.min(total, Math.max(0, toAbsoluteLine(program.cursor) - startLine))

  // THROUGH YESTERDAY: today's ward is not owed until the day is over.
  const expected = Math.min(total, expectedLinesBy(program, today - 1))
  const perDay = nominalLines(
    currentSegment(program, today).unit,
    currentSegment(program, today).amount,
  )

  // DEBT IS CAPPED. Uncapped carry-over produces a 40-page day that ends the habit.
  const debt = Math.min(Math.max(0, expected - done), perDay * program.maxCarryOver)
  const ahead = Math.max(0, done - expected)
  const percent = total > 0 ? Math.round((done / total) * 100) : 0

  // A rate of zero has NO finish date rather than an infinite one — "∞ days" is worse
  // than showing nothing.
  const daysLeft = perDay > 0 ? Math.ceil((total - done) / perDay) : null
  const finishesOn = daysLeft === null ? null : now + daysLeft * DAY_MS

  return {
    total,
    done,
    expected,
    perDay,
    debt,
    ahead,
    percent,
    daysLeft,
    finishesOn,
    scope,
    startLine,
    endLine,
  }
}

// ---------------------------------------------------------------------------
// §11.4 — today's ward
// ---------------------------------------------------------------------------

export type WardBlockId = 'sabq' | 'sabqi' | 'dhor'

/**
 * Why a block's note is a LIST OF DESCRIPTORS and not a sentence (I3).
 *
 * The scheduler used to return the rendered Arabic — `المقرّر اليوم مع 3 صفحة مؤجّلة`
 * — so a pure scheduling function's return value was a sentence rather than a fact
 * about the schedule, and a test could only assert a substring of prose. A descriptor
 * says WHAT IS TRUE (`{ k: 'sabq.carried', p: { pages: 3 } }`); the reader says it in
 * Arabic (`apps/web/src/content/ward.ts`).
 *
 * A LIST, because the notes genuinely compose: a سبق portion can be both carried over
 * and worked ahead, and a دور rotation can pick up lapsed pages on top of its section.
 * The reader joins them with « · ».
 *
 * The LABEL is gone entirely — it is derivable from `id` and the programme's `kind`,
 * which is what `wardBlockLabel` does.
 */
export type WardNote =
  /** Today's portion, plus `pages` pages carried over from days missed. */
  | { k: 'sabq.carried'; p: { pages: number } }
  /** Today's portion, nothing owed. */
  | { k: 'sabq.new' }
  /** `days` extra days taken voluntarily, on top of what is owed. */
  | { k: 'sabq.ahead'; p: { days: number } }
  /** The `pages` pages immediately behind the cursor, revised daily. */
  | { k: 'sabqi.window'; p: { pages: number } }
  | { k: 'dhor.rest' }
  | { k: 'dhor.rotationBatch'; p: { days: number } }
  | { k: 'dhor.rotationDaily' }
  | { k: 'dhor.srs' }
  /** `take` of `n` sections at once, because `take - 1` days were asked for ahead. */
  | { k: 'dhor.sectionsBatch'; p: { take: number; n: number } }
  /** Section `section` of `n`, 1-based. */
  | { k: 'dhor.section'; p: { section: number; n: number } }
  /** The far review fires today; it comes round every `every` days. */
  | { k: 'dhor.windowFires'; p: { every: number } }
  /** Not today; the far review comes round every `every` days. */
  | { k: 'dhor.windowWaits'; p: { every: number } }
  | { k: 'dhor.rateBatch'; p: { days: number } }
  | { k: 'dhor.rate' }
  /** SM-2 pulled `pages` lapsed pages forward, on top of the rotation. */
  | { k: 'dhor.lapsed'; p: { pages: number } }

export interface WardBlock {
  id: WardBlockId
  pages: number[]
  span: { from: LinePosition; to: LinePosition } | null
  notes: WardNote[]
}

export interface Ward {
  day: number
  restDay: boolean
  newPaused: boolean
  overdue: number
  carried: number
  caughtUp: boolean
  daysAhead: number
  aheadDays: number
  blocks: WardBlock[]
  adherence: Adherence
}

export interface WardContext {
  index: Meta
  srs: Iterable<SrsCard>
  memorised: number[]
  now: number
}

const pageRange = (from: number, to: number): number[] => {
  if (to < from) return []
  return Array.from({ length: to - from + 1 }, (_, i) => from + i)
}

export function todaysWard(
  program: Program,
  ctx: WardContext,
  options: { aheadDays?: number } = {},
): Ward {
  const { index, memorised, now } = ctx
  const today = dayStamp(now)
  const method = methodById(program.methodId)
  const adh = adherence(program, index, now)
  const scope = adh.scope

  const dayIndex = Math.max(0, today - program.startedOn)
  const restDay = isRestDay(today, program.restDays)
  const segment = currentSegment(program, today)

  const due = dueCards(ctx.srs, now).filter((c) => c.page >= scope.from && c.page <= scope.to)
  const pauseNewAt = method?.pauseNewAt ?? 0
  const newPaused = pauseNewAt > 0 && due.length >= pauseNewAt && program.kind === 'hifz'

  const aheadDays = Math.min(MAX_AHEAD_DAYS, Math.max(0, Math.round(options.aheadDays ?? 0)))
  const daysAhead = adh.perDay > 0 ? Math.floor(adh.ahead / adh.perDay) : 0

  /**
   * `state === 'done'` MUST be part of `advances`. `advanceCursor` parks a completed
   * programme on the LAST line of its scope rather than one past it (so the final
   * page can still be settled), which leaves exactly one line between the cursor and
   * the end — and without this guard the ward goes on offering that one line as a
   * portion, every day, for ever. (Real bug: a repeating loop over a five-page scope
   * produced 50 sessions instead of 5.)
   */
  const advances =
    (program.kind === 'hifz' || program.kind === 'tilawah') && program.state !== 'done'

  const cursorLine = toAbsoluteLine(program.cursor)
  const blocks: WardBlock[] = []

  // ---- Block 1 — سبق (new) ------------------------------------------------
  const base = advances ? volumeSpan(segment.unit, segment.amount, cursorLine, index) : 0
  const carried = restDay || newPaused ? 0 : Math.round(adh.debt)
  const obligation =
    restDay || newPaused || !advances ? 0 : Math.max(0, base + carried - Math.round(adh.ahead))
  const voluntary =
    advances && !newPaused && aheadDays > 0
      ? volumeSpan(segment.unit, segment.amount * aheadDays, cursorLine + obligation, index)
      : 0

  // SUBTRACTING `adherence.ahead` IS WHAT MAKES WORKING AHEAD COUNT. A reader who
  // does three days in one sitting owes nothing for the next two. Before this, the
  // plan handed out a full portion every day no matter how far ahead the cursor was —
  // getting ahead was silently punished.
  const caughtUp = advances && !newPaused && !restDay && base > 0 && obligation === 0
  const sabqSpan = Math.max(0, Math.min(obligation + voluntary, adh.endLine - cursorLine))

  if (advances && sabqSpan > 0) {
    const notes: WardNote[] = [
      carried > 0
        ? { k: 'sabq.carried', p: { pages: Math.max(1, Math.round(carried / 15)) } }
        : { k: 'sabq.new' },
    ]
    if (voluntary > 0) notes.push({ k: 'sabq.ahead', p: { days: aheadDays } })

    blocks.push({
      id: 'sabq',
      pages: [],
      span: {
        from: fromAbsoluteLine(cursorLine),
        to: fromAbsoluteLine(cursorLine + sabqSpan - 1),
      },
      notes,
    })
  }

  // ---- Block 2 — سبقي (recent), hifz only ---------------------------------
  let recentCount = 0
  if (program.kind === 'hifz' && method) {
    const recent = method.recent
    recentCount =
      recent.type === 'none'
        ? 0
        : recent.type === 'pages'
          ? recent.n
          : // Resolved against the rate IN FORCE, so widening the daily portion widens
            // the window that portion has to be revised in.
            Math.max(1, Math.round((recent.n * adh.perDay) / 15))

    if (recentCount > 0) {
      const recentTo = Math.max(scope.from, program.cursor.page - 1)
      const recentFrom = Math.max(scope.from, program.cursor.page - recentCount)
      const pages = program.cursor.page > scope.from ? pageRange(recentFrom, recentTo) : []
      if (pages.length > 0) {
        blocks.push({
          id: 'sabqi',
          pages,
          span: null,
          notes: [{ k: 'sabqi.window', p: { pages: pages.length } }],
        })
      }
    }
  }

  // ---- Block 3 — دور (old) ------------------------------------------------
  const dhor = buildDhor({
    program,
    method,
    index,
    scope,
    dayIndex,
    aheadDays,
    restDay,
    recentCount,
    memorised,
    due,
    segment,
    cursorLine,
    adh,
  })
  // Emit the دور block if it has pages OR a note — the note alone tells the reader
  // when the far review is next due.
  if (dhor && (dhor.pages.length > 0 || dhor.notes.length > 0)) blocks.push(dhor)

  return {
    day: today,
    restDay,
    newPaused,
    overdue: due.length,
    carried,
    caughtUp,
    daysAhead,
    aheadDays,
    blocks,
    adherence: adh,
  }
}

interface DhorInput {
  program: Program
  method: HifzMethod | undefined
  index: Meta
  scope: { from: number; to: number }
  dayIndex: number
  aheadDays: number
  restDay: boolean
  recentCount: number
  memorised: number[]
  due: SrsCard[]
  segment: VolumeSegment
  cursorLine: number
  adh: Adherence
}

function buildDhor(input: DhorInput): WardBlock | null {
  const {
    program,
    method,
    index,
    scope,
    dayIndex,
    aheadDays,
    restDay,
    recentCount,
    memorised,
    due,
    segment,
    cursorLine,
    adh,
  } = input

  // A murājaʿah rotation: THE CURSOR IS THE ROTATION. It runs over the whole scope
  // rather than over what is marked memorised — these methods are for people who have
  // already finished. A rotation has no debt and nothing to bank; it simply covers
  // more ground when more days are asked for.
  if (program.kind === 'murajaah') {
    if (restDay && aheadDays === 0) {
      return { id: 'dhor', pages: [], span: null, notes: [{ k: 'dhor.rest' }] }
    }
    const scopeStartLine = linesBefore(scope.from)
    const scopeEndLine = linesBefore(scope.to + 1)
    const scopeLength = Math.max(1, scopeEndLine - scopeStartLine)
    const span = volumeSpan(segment.unit, segment.amount * (1 + aheadDays), cursorLine, index)
    const offset = (((cursorLine - scopeStartLine) % scopeLength) + scopeLength) % scopeLength
    const start = scopeStartLine + offset
    const end = Math.min(scopeEndLine - 1, start + Math.max(1, span) - 1)
    return {
      id: 'dhor',
      pages: [],
      span: { from: fromAbsoluteLine(start), to: fromAbsoluteLine(end) },
      notes: [
        aheadDays > 0
          ? { k: 'dhor.rotationBatch', p: { days: 1 + aheadDays } }
          : { k: 'dhor.rotationDaily' },
      ],
    }
  }

  if (program.kind !== 'hifz' || !method) return null

  const ceiling = Math.max(0, segment.review)
  const recentPages = new Set(
    pageRange(
      Math.max(scope.from, program.cursor.page - recentCount),
      Math.max(scope.from, program.cursor.page - 1),
    ),
  )
  const inScope = (p: number) => p >= scope.from && p <= scope.to
  const pool = memorised.filter((p) => inScope(p) && !recentPages.has(p)).sort((a, b) => a - b)

  let pages: number[] = []
  let notes: WardNote[] = []
  let positional = true

  switch (method.old.type) {
    case 'srs': {
      positional = false
      pages = due.map((c) => c.page).filter((p) => !recentPages.has(p))
      if (ceiling > 0) pages = pages.slice(0, ceiling)
      notes = [{ k: 'dhor.srs' }]
      break
    }

    case 'sections': {
      const n = Math.max(1, method.old.n)
      if (pool.length > 0) {
        const size = Math.ceil(pool.length / n)
        // Capped at ONE FULL CYCLE, because going round twice in a day revises
        // nothing new.
        const take = Math.min(n, 1 + aheadDays)
        const first = dayIndex % n
        const picked = new Set<number>()
        for (let i = 0; i < take; i += 1) {
          const section = (first + i) % n
          for (const p of pool.slice(section * size, (section + 1) * size)) picked.add(p)
        }
        pages = [...picked].sort((a, b) => a - b)
        notes = [
          take > 1
            ? { k: 'dhor.sectionsBatch', p: { take, n } }
            : { k: 'dhor.section', p: { section: first + 1, n } },
        ]
      } else {
        notes = [{ k: 'dhor.section', p: { section: (dayIndex % n) + 1, n } }]
      }
      break
    }

    case 'window': {
      const every = Math.max(1, method.old.every)
      let fires = false
      for (let d = 0; d <= aheadDays; d += 1) {
        if ((dayIndex + d) % every === 0) fires = true
      }
      if (fires) {
        const to = Math.max(scope.from, program.cursor.page - recentCount - 1)
        const from = Math.max(scope.from, to - method.old.back + 1)
        pages = pageRange(from, to)
        notes = [{ k: 'dhor.windowFires', p: { every } }]
      } else {
        notes = [{ k: 'dhor.windowWaits', p: { every } }]
      }
      break
    }

    case 'rate': {
      if (pool.length > 0) {
        const per = Math.max(1, Math.round(nominalLines(method.old.unit, method.old.amount) / 15))
        const take = Math.min(per * (1 + aheadDays), pool.length)
        const start = (dayIndex * per) % pool.length
        const picked: number[] = []
        for (let i = 0; i < take; i += 1) picked.push(pool[(start + i) % pool.length]!)
        pages = [...new Set(picked)].sort((a, b) => a - b)
      }
      notes = [
        aheadDays > 0 ? { k: 'dhor.rateBatch', p: { days: 1 + aheadDays } } : { k: 'dhor.rate' },
      ]
      break
    }
  }

  /**
   * Then, for every non-`srs` old policy, SM-2 PULLS LAPSED PAGES FORWARD: a page you
   * have actually blanked on should not wait for its section to come round.
   *
   * A positional rotation is self-bounding so it is NOT capped — the ceiling exists
   * to bound the UNBOUNDED thing, which is the SRS pile.
   */
  if (positional) {
    const have = new Set(pages)
    const lapsed = due.filter((c) => c.lapses > 0 && !have.has(c.page)).map((c) => c.page)
    const extra = ceiling > 0 ? lapsed.slice(0, ceiling) : lapsed
    if (extra.length > 0) {
      pages = [...new Set([...pages, ...extra])].sort((a, b) => a - b)
      notes = [...notes, { k: 'dhor.lapsed', p: { pages: extra.length } }]
    }
  }

  void adh
  return { id: 'dhor', pages, span: null, notes }
}

// ---------------------------------------------------------------------------
// §11.5 — mutations
// ---------------------------------------------------------------------------

export interface CreateProgramInput {
  id: string
  methodId: string
  /**
   * I3: this used to default to `method.name`, which is why the method library carried
   * Arabic names at all. The reader seeds it from `METHOD_COPY`; omitting it falls back
   * to the method id, which is a legible placeholder and never Arabic prose in here.
   */
  name?: string
  scope?: ProgramScope
  volume?: { unit: VolumeUnit; amount: number; review: number }
  cursor?: LinePosition
  restDays?: number[]
  maxCarryOver?: number
  now: number
}

export function createProgram(input: CreateProgramInput): Program {
  const method = methodById(input.methodId)
  if (!method) throw new Error(`unknown method: ${input.methodId}`)

  const startedOn = dayStamp(input.now)
  const cursor = input.cursor ?? { page: 1, line: 1 }
  const volume = input.volume ?? method.defaultVolume

  return {
    id: input.id,
    methodId: method.id,
    name: input.name?.trim() || method.id,
    kind: method.kind,
    scope: input.scope ?? { type: 'mushaf' },
    volumes: [{ from: startedOn, ...volume }],
    cursor: { ...cursor },
    startAt: { ...cursor },
    restDays: input.restDays ?? [],
    maxCarryOver: input.maxCarryOver ?? DEFAULT_MAX_CARRY_OVER,
    state: 'active',
    startedOn,
    createdAt: input.now,
  }
}

/**
 * An edit on a day that already has a segment REPLACES it — the stepper fires on
 * every press, and without the collapse, nudging 1 → 4 writes four segments for one
 * day (T50). Changing the unit also opens a new segment.
 */
export function setVolume(
  program: Program,
  volume: { unit: VolumeUnit; amount: number; review: number },
  now: number,
): Program {
  const today = dayStamp(now)
  const kept = program.volumes.filter((v) => v.from !== today)
  return {
    ...program,
    volumes: [...kept, { from: today, ...volume }].sort((a, b) => a.from - b.from),
  }
}

export function advanceCursor(program: Program, lines: number, index: Meta): Program {
  const scope = scopePages(program.scope, index)
  const scopeStart = linesBefore(scope.from)
  const scopeEnd = linesBefore(scope.to + 1)
  const next = toAbsoluteLine(program.cursor) + Math.max(0, Math.round(lines))

  // A murājaʿah rotation WRAPS — the cycle is the point, and it never marks itself
  // done (T57).
  if (program.kind === 'murajaah') {
    const length = Math.max(1, scopeEnd - scopeStart)
    const wrapped = scopeStart + ((((next - scopeStart) % length) + length) % length)
    return { ...program, cursor: fromAbsoluteLine(wrapped) }
  }

  if (next >= scopeEnd) {
    // Park on the LAST line of the scope rather than one past it, so the final page
    // can still be settled. `advances` in todaysWard guards the consequence.
    return { ...program, cursor: fromAbsoluteLine(scopeEnd - 1), state: 'done' }
  }
  return { ...program, cursor: fromAbsoluteLine(Math.max(scopeStart, next)) }
}

export const blockLines = (block: WardBlock): number =>
  block.span ? toAbsoluteLine(block.span.to) - toAbsoluteLine(block.span.from) + 1 : 0

/**
 * A page counts as finished ONLY once the cursor has moved PAST ITS LAST LINE.
 *
 * Deriving it from `fromAbsoluteLine(cursor - 1).page` returns the cursor's own page
 * whenever the cursor is mid-page — so a line-unit programme doing 8 of a page's 15
 * lines would have marked the page memorised, enrolled it in the SRS, and scheduled
 * revisions of lines never read (T47). `finished` is the one exception, because
 * `advanceCursor` clamps a completed programme to the last line of its scope rather
 * than one past it.
 */
export function settledPages(from: LinePosition, to: LinePosition, finished: boolean): number[] {
  const last = finished ? to.page : to.page - 1
  return from.page > last ? [] : pageRange(from.page, last)
}
